/**
 * The boat-class vocabulary, and the one rule that makes it dangerous.
 *
 * `GET /api/boats/classes` returns TWO kinds of slug. The canonical 24 are
 * writable. The rest — `jon boat`, `skiff`, `convertible`, `fish & ski`,
 * `bass boat`, `center console`, `deep-v` — are dirty values that exist in
 * `boat_model` today and are returned SO A ROW'S CURRENT VALUE CAN BE SHOWN.
 *
 * Every write path rejects them with 400 UNKNOWN_BOAT_CLASS. You may reassign
 * FROM `bass boat`; you may never assign TO it. A <select> built naively from
 * this response looks correct and fails on save.
 *
 * That rule lives here and nowhere else, so there is one place to test it and
 * one place to change it when the dirty values are cleaned up before go-live.
 *
 * ZERO IMPORTS BY DESIGN — unit-tested directly by
 * `scripts/test/marineClassOptions.test.mjs`. Do not add imports.
 */

/** Marks a dirty value in a dropdown so the reason it is greyed out is visible. */
export const NEEDS_CLEANUP_SUFFIX = ' — needs cleanup';

const byLabel = (a, b) => String(a.label).localeCompare(String(b.label));

/**
 * Normalize the classes response.
 *
 * `needs_cleanup` is authoritative when present. When it is absent — a bare
 * array, or an older envelope — derive it from `canonical: false` rather than
 * assuming everything is writable, because assuming the permissive answer here
 * produces a dropdown that 400s.
 */
export function parseClasses(payload) {
  const rows = Array.isArray(payload)
    ? payload
    : (payload && Array.isArray(payload.data) ? payload.data : []);
  if (rows.length === 0) return { all: [], canonical: [], needsCleanup: [] };

  const all = rows.filter(Boolean).map((r) => ({
    slug: r.slug,
    label: r.label || r.slug,
    canonical: r.canonical === true,
    inUseCount: Number(r.in_use_count) || 0,
  }));

  const declared = payload && Array.isArray(payload.needs_cleanup) ? payload.needs_cleanup : null;
  return {
    all,
    canonical: all.filter((c) => c.canonical).sort(byLabel),
    needsCleanup: declared || all.filter((c) => !c.canonical).map((c) => c.slug),
  };
}

/** The only slugs a write may target. */
export function writeTargets(parsed) {
  return (parsed && parsed.canonical ? parsed.canonical : []).map(({ slug, label }) => ({ slug, label }));
}

/** The 400 guard. Anything not canonical — including unknown — is unwritable. */
export function isWritable(parsed, slug) {
  if (!slug) return false;
  return (parsed && parsed.canonical ? parsed.canonical : []).some((c) => c.slug === slug);
}

/** Display label for any slug, canonical or not. Unknown slugs show themselves. */
export function labelFor(parsed, slug) {
  if (!slug) return '';
  const hit = (parsed && parsed.all ? parsed.all : []).find((c) => c.slug === slug);
  return hit ? hit.label : String(slug);
}

/**
 * Options for one row's class select.
 *
 * Canonical slugs, plus — only when the row currently holds a dirty value — that
 * value as a DISABLED option. Without it the select renders blank and the row
 * looks unclassified when it is not; with it enabled, saving 400s.
 */
export function optionsForRow(parsed, currentSlug) {
  const targets = writeTargets(parsed).map((t) => ({ ...t, disabled: false }));
  if (!currentSlug || isWritable(parsed, currentSlug)) return targets;
  return [
    { slug: currentSlug, label: `${labelFor(parsed, currentSlug)}${NEEDS_CLEANUP_SUFFIX}`, disabled: true },
    ...targets,
  ];
}
